import React, { useEffect, useMemo, useState } from 'react';
// Unified Portal — OLM1 inbound appointments from Andes
// (isvs_ddl.o_appointment_details), grouped by EAD (earliest arrival) date in
// PST. One table per day with a date-range selector. Cancelled appts are
// excluded upstream by the backend sync (unified-portal-sync.sh).

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// "2026-10-21 07:00:00" or "2026-10-21" -> "21-Oct"; empty -> "-"
function dmon(s) {
  if (!s || s.length < 10) return '-';
  const y = +s.slice(0, 4), m = +s.slice(5, 7), d = +s.slice(8, 10);
  if (!m || !d) return '-';
  return `${d}-${MONTHS[m - 1]}`;
}
// today in PST as yyyy-mm-dd
function ptToday() {
  const p = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Los_Angeles' }));
  return `${p.getFullYear()}-${String(p.getMonth() + 1).padStart(2, '0')}-${String(p.getDate()).padStart(2, '0')}`;
}
function addDays(iso, n) {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}
const nz = (v) => (v == null ? 0 : v);

// "ARRIVAL_SCHEDULED" -> "Arrival Scheduled"; "CHECKED_IN" -> "Checked In".
function statusLabel(s) {
  if (!s || s === '(none)') return 'No Status';
  return s.split('_').map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
}

export default function UnifiedPortal({ api, site }) {
  const [raw, setRaw] = useState(null);
  const [err, setErr] = useState('');
  // default range: today-1 .. today+7 (a planning-forward window)
  const [from, setFrom] = useState(addDays(ptToday(), -1));
  const [to, setTo] = useState(addDays(ptToday(), 7));
  // Per-day collapse state: { [day]: true=collapsed }. Days start collapsed.
  const [open, setOpen] = useState({});
  const toggle = (day) => setOpen((o) => ({ ...o, [day]: !o[day] }));

  useEffect(() => {
    let alive = true;
    setErr('');
    // Appts are chunked across UNIFIED_PORTAL_<site>_<n> items (DynamoDB's
    // 400KB/item cap can't hold a full 60-day pull). Chunk 0 carries chunkCount
    // + generatedAt; merge all chunks' appts. Falls back to the legacy single
    // UNIFIED_PORTAL_<site> item if no chunked data exists.
    (async () => {
      try {
        const first = await fetch(`${api}/api/fclm/cache?site=${site}&date=UNIFIED_PORTAL_${site}_0&shift_type=data`).then((r) => r.json());
        if (first && Array.isArray(first.appts)) {
          let appts = first.appts.slice();
          const count = first.chunkCount || 1;
          for (let i = 1; i < count; i++) {
            try {
              const c = await fetch(`${api}/api/fclm/cache?site=${site}&date=UNIFIED_PORTAL_${site}_${i}&shift_type=data`).then((r) => r.json());
              if (c && Array.isArray(c.appts)) appts = appts.concat(c.appts);
            } catch (e) { /* skip a bad chunk */ }
          }
          if (alive) setRaw({ appts, generatedAt: first.generatedAt, windowDays: first.windowDays });
          return;
        }
      } catch (e) { /* fall through to legacy */ }
      try {
        const d = await fetch(`${api}/api/fclm/cache?site=${site}&date=UNIFIED_PORTAL_${site}&shift_type=data`).then((r) => r.json());
        if (alive) setRaw(d && Array.isArray(d.appts) ? d : { appts: [] });
      } catch (e) {
        if (alive) { setRaw({ appts: [] }); setErr('Failed to load Unified Portal data.'); }
      }
    })();
    return () => { alive = false; };
  }, [api, site]);

  // Group filtered appts by EAD date, build per-day totals + sorted rows.
  const days = useMemo(() => {
    const appts = (raw && raw.appts) || [];
    const byDay = {};
    for (const a of appts) {
      const day = a.eadDate;
      if (!day) continue;
      if (from && day < from) continue;
      if (to && day > to) continue;
      (byDay[day] = byDay[day] || []).push(a);
    }
    return Object.keys(byDay).sort().map((day) => {
      const rows = byDay[day].slice().sort((x, y) => (x.ead || '').localeCompare(y.ead || ''));
      const dropRows = rows.filter((r) => r.isDrop);
      const drops = dropRows.length;
      const units = rows.reduce((s, r) => s + nz(r.units), 0);
      const pallets = rows.reduce((s, r) => s + nz(r.pallets), 0);
      // Per-status breakdown of the DROPS (so the sub-counts sum to `drops`).
      const byStatus = {};
      for (const r of dropRows) {
        const s = r.status || '(none)';
        byStatus[s] = (byStatus[s] || 0) + 1;
      }
      const statusCounts = Object.keys(byStatus).sort().map((s) => ({ status: s, n: byStatus[s] }));
      return { day, rows, drops, units, pallets, statusCounts };
    });
  }, [raw, from, to]);

  const totalAppts = days.reduce((s, d) => s + d.rows.length, 0);

  const cell = { border: '1px solid #ccc', padding: '3px 8px', fontSize: '0.78rem', whiteSpace: 'nowrap' };
  const num = { ...cell, textAlign: 'right' };
  const hdr = { ...cell, background: '#232f3e', color: '#fff', fontWeight: 600, textAlign: 'center' };

  return (
    <div>
      <h2 className="section-title">Unified Portal — Inbound Appointments (EAD, PST) — {site}</h2>

      <div className="card" style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
        <label style={{ fontSize: '0.75rem' }}>From (EAD): <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ fontSize: '0.75rem' }} /></label>
        <label style={{ fontSize: '0.75rem' }}>To (EAD): <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ fontSize: '0.75rem' }} /></label>
        <span style={{ fontSize: '0.72rem', color: '#555' }}>{totalAppts} appointments · {days.length} day(s) · cancelled excluded</span>
        {days.length > 0 && (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <button onClick={() => setOpen(Object.fromEntries(days.map((d) => [d.day, true])))} style={{ fontSize: '0.7rem', cursor: 'pointer' }}>Expand all</button>
            <button onClick={() => setOpen({})} style={{ fontSize: '0.7rem', cursor: 'pointer' }}>Collapse all</button>
          </span>
        )}
        {raw && raw.generatedAt && <span style={{ fontSize: '0.65rem', color: '#888' }}>data as of {dmon(raw.generatedAt)} (synced hourly)</span>}
      </div>

      {err && <div className="card" style={{ marginBottom: 8, background: '#fdecea', color: '#c0392b', fontSize: '0.8rem' }}>{err}</div>}

      {!err && days.length === 0 && (
        <div className="card" style={{ marginBottom: 8, fontSize: '0.8rem', color: '#666' }}>
          No appointments with an EAD in this range. Widen the date range, or the hourly sync may not have populated yet.
        </div>
      )}

      {days.map(({ day, rows, drops, units, pallets, statusCounts }) => {
        const isOpen = !!open[day];
        return (
        <div key={day} className="card" style={{ marginBottom: 8, overflowX: 'auto', padding: 0 }}>
          {/* Collapsible per-day banner: click to toggle. Starts collapsed. */}
          <div
            onClick={() => toggle(day)}
            style={{ display: 'flex', alignItems: 'center', gap: 16, cursor: 'pointer', background: '#e8eef5', padding: '6px 10px', fontSize: '0.8rem', fontWeight: 700, flexWrap: 'wrap' }}
          >
            <span style={{ width: 12, display: 'inline-block' }}>{isOpen ? '▾' : '▸'}</span>
            <span style={{ minWidth: 70 }}>{dmon(day)}</span>
            <span style={{ fontWeight: 400, color: '#555' }}>{rows.length} appt{rows.length === 1 ? '' : 's'}</span>
            <span>
              Total Drops: <strong>{drops}</strong>
              {statusCounts.length > 0 && (
                <span style={{ fontWeight: 400, color: '#555' }}>
                  {' '}({statusCounts.map((s) => `${statusLabel(s.status)} ${s.n}`).join(' | ')})
                </span>
              )}
            </span>
            <span>Units: <strong>{units.toLocaleString()}</strong></span>
            <span>Pallets: <strong>{pallets.toLocaleString()}</strong></span>
          </div>
          {isOpen && (
          <table style={{ borderCollapse: 'collapse', width: '100%' }}>
            <thead>
              <tr>
                <th style={hdr}>ISA</th>
                <th style={hdr}>SCAC</th>
                <th style={hdr}>Useable Trailer ID</th>
                <th style={hdr}>EAD</th>
                <th style={hdr}>SAD</th>
                <th style={hdr}>LU</th>
                <th style={hdr}>Actual Arrival</th>
                <th style={hdr}>Closed</th>
                <th style={hdr}>Load Type</th>
                <th style={hdr}>Status</th>
                <th style={hdr}>Units</th>
                <th style={hdr}>Pallets</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.apptId}>
                  <td style={{ ...cell, fontFamily: 'monospace' }}>{r.isa || '-'}</td>
                  <td style={cell}>{r.scac || '-'}</td>
                  <td style={{ ...cell, fontFamily: 'monospace' }}>{r.usableTrailerId || r.trailerId || '-'}</td>
                  <td style={cell}>{dmon(r.ead)}</td>
                  <td style={cell}>{dmon(r.sad)}</td>
                  <td style={cell}>{dmon(r.lu)}</td>
                  <td style={cell}>{dmon(r.actualArrival)}</td>
                  <td style={cell}>{dmon(r.closed)}</td>
                  <td style={cell}>{r.loadType || '-'}{r.isDrop ? ' · Drop' : ''}</td>
                  <td style={cell}>{r.status || '-'}</td>
                  <td style={num}>{r.units != null ? r.units.toLocaleString() : '-'}</td>
                  <td style={num}>{r.pallets != null ? r.pallets.toLocaleString() : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          )}
        </div>
        );
      })}
    </div>
  );
}
